import {
  huge,
  hugeZero,
  maxHuge,
  type HugeNumber,
  type HugeNumberInput,
} from "@/shared/numbers";

interface HovRateSample {
  readonly elapsedSeconds: number;
  readonly hovEarned: HugeNumber;
}

const recentHovRateSegmentTokenBrand = Symbol("recentHovRateSegmentToken");

export interface RecentHovRateSegmentToken {
  readonly [recentHovRateSegmentTokenBrand]: true;
}

export interface CompleteRecentHovRateSegmentOptions {
  readonly segmentEndCurrentLoopHovEarned: HugeNumberInput;
  readonly nextCurrentLoopHovEarned: HugeNumberInput;
  readonly finalGameplayElapsedSeconds?: number;
}

const RECENT_HOV_RATE_WINDOW_SECONDS = 5;
const RECENT_HOV_RATE_UPDATE_INTERVAL_SECONDS = 1;

export interface RecentHovRateTracker {
  advance(dtSeconds: number, hovEarnedThisTick: HugeNumberInput): void;
  completeSegmentAndReset(
    options: CompleteRecentHovRateSegmentOptions,
  ): RecentHovRateSegmentToken | null;
  reset(): void;
  getRate(): HugeNumber;
  getPresentationRate(
    segmentToken: RecentHovRateSegmentToken | null,
  ): HugeNumber;
}

export function createRecentHovRateTracker(): RecentHovRateTracker {
  let elapsedSeconds = 0;
  let updateAccumulator = 0;
  let pendingHovEarned = hugeZero();
  let rate = hugeZero();
  let samples: HovRateSample[] = [{ elapsedSeconds: 0, hovEarned: hugeZero() }];
  let segmentRates = new WeakMap<RecentHovRateSegmentToken, HugeNumber>();

  const resetCurrentRun = (): void => {
    elapsedSeconds = 0;
    updateAccumulator = 0;
    pendingHovEarned = hugeZero();
    rate = hugeZero();
    samples = [{ elapsedSeconds: 0, hovEarned: hugeZero() }];
  };

  // Each sample carries the Hov actually earned during its interval, not a
  // cumulative balance. Summing per-interval gains avoids subtracting two huge
  // near-equal totals, where a small increment (e.g. 1e324 against a 1e359 loop
  // total) rounds away in break_eternity and freezes the rate at zero.
  const recomputeRate = (): void => {
    const newest = samples.at(-1);
    const oldest = samples[0];
    if (!newest || !oldest) {
      rate = hugeZero();
      return;
    }

    const windowSeconds = newest.elapsedSeconds - oldest.elapsedSeconds;
    if (windowSeconds <= 0) {
      rate = hugeZero();
      return;
    }

    let windowHovEarned = hugeZero();
    for (let index = 1; index < samples.length; index += 1) {
      windowHovEarned = windowHovEarned.add(samples[index]!.hovEarned);
    }

    rate = maxHuge(hugeZero(), windowHovEarned.div(windowSeconds));
  };

  return {
    advance(dtSeconds: number, hovEarnedThisTick: HugeNumberInput): void {
      elapsedSeconds += dtSeconds;
      updateAccumulator += dtSeconds;
      pendingHovEarned = pendingHovEarned.add(huge(hovEarnedThisTick));

      if (updateAccumulator < RECENT_HOV_RATE_UPDATE_INTERVAL_SECONDS) {
        return;
      }

      samples.push({ elapsedSeconds, hovEarned: pendingHovEarned });
      pendingHovEarned = hugeZero();
      updateAccumulator = 0;

      const minElapsedSeconds = elapsedSeconds - RECENT_HOV_RATE_WINDOW_SECONDS;
      while (
        samples.length >= 2 &&
        samples[1]!.elapsedSeconds <= minElapsedSeconds
      ) {
        samples.shift();
      }

      recomputeRate();
    },

    completeSegmentAndReset(
      options: CompleteRecentHovRateSegmentOptions,
    ): RecentHovRateSegmentToken | null {
      const finalGameplayElapsedSeconds =
        options.finalGameplayElapsedSeconds ?? 0;
      const segmentGameplayElapsedSeconds =
        elapsedSeconds + finalGameplayElapsedSeconds;
      const segmentEndCurrentLoopHovEarned = huge(
        options.segmentEndCurrentLoopHovEarned,
      );
      let segmentToken: RecentHovRateSegmentToken | null = null;

      if (
        Number.isFinite(finalGameplayElapsedSeconds) &&
        finalGameplayElapsedSeconds >= 0 &&
        Number.isFinite(segmentGameplayElapsedSeconds) &&
        segmentGameplayElapsedSeconds > 0 &&
        segmentEndCurrentLoopHovEarned.isFinite() &&
        segmentEndCurrentLoopHovEarned.gte(0)
      ) {
        const segmentRate = segmentEndCurrentLoopHovEarned.div(
          segmentGameplayElapsedSeconds,
        );
        if (segmentRate.isFinite() && segmentRate.gte(0)) {
          segmentToken = {
            [recentHovRateSegmentTokenBrand]: true,
          };
          segmentRates.set(segmentToken, segmentRate);
        }
      }

      resetCurrentRun();
      return segmentToken;
    },

    reset(): void {
      segmentRates = new WeakMap<RecentHovRateSegmentToken, HugeNumber>();
      resetCurrentRun();
    },

    getRate(): HugeNumber {
      return rate;
    },

    getPresentationRate(
      segmentToken: RecentHovRateSegmentToken | null,
    ): HugeNumber {
      if (segmentToken === null) {
        return rate;
      }
      return segmentRates.get(segmentToken) ?? rate;
    },
  };
}
